import _ from 'lodash';

import { CoreEnums } from '@/utils/domain/constants';
import { sortFn } from '@/utils/infrastructure';
import { fieldConfigurationMapping } from '../field-configuration';
import {
  APPEARANCE_TYPES,
  createBehaviorConfig,
  findApplicableBehavior,
  getFieldInitialBehavior,
  hasValidConditions,
} from './fieldBehaviors';
import { mapTableFields } from './tableFieldHelpers';
import { mergeUniqueRules } from './utils';

const { FormFieldType, FormFieldBehavior, FormFieldStringType } = CoreEnums;

const AUTO_CALC_FIELD_STYLE = { marginTop: 32, marginBottom: 0 };

export const isTableField = (fieldType) =>
  fieldType === FormFieldType.Table || fieldType === FormFieldStringType.Table;

export const isAutoCalculatedField = (fieldType) =>
  fieldType === FormFieldType.AutoCalculatedByTableColumn ||
  fieldType === FormFieldStringType.AutoCalculatedByTableColumn;

export const createFieldBase = (
  field,
  { operationType, formId, processId, versionId, instanceData },
) => ({
  ...field,
  versionId,
  processId,
  operationType,
  formId,
  value: field.value ?? instanceData?.[field.internalName] ?? field.defaultValue,
});

export function fieldIterator(field, config) {
  const behaviors = _.filter(
    config?.behaviorCases,
    (behaviorCase) => behaviorCase.field?.id === field?.id,
  );

  const shouldUpdate = _.some(behaviors, hasValidConditions);
  const applicableBehavior = findApplicableBehavior(behaviors);
  const fieldBehavior = applicableBehavior?.fieldBehavior || null;

  return {
    ...field,
    shouldUpdate,
    ...(fieldBehavior && { fieldBehavior }),
  };
}

export function mapField(field, config, operationType) {
  if (isTableField(field?.type)) {
    return mapTableFields(field, config, operationType);
  }

  const { form, data: instanceData, behaviorCases, versionId, processId } = config;
  const initialBehavior = getFieldInitialBehavior(field);
  const fieldBase = createFieldBase(field, {
    operationType,
    formId: form?.id,
    processId,
    versionId,
    instanceData,
  });

  const hasLongText = typeof field.longText === 'string' && field.longText.trim().length > 0;
  const fieldConfig = fieldConfigurationMapping({ ...fieldBase, ...initialBehavior }, form.fields);
  const behaviorConfig = createBehaviorConfig(
    field,
    behaviorCases,
    initialBehavior?.isLocked,
    field.shouldUpdate,
  );

  return {
    ...fieldConfig,
    ...initialBehavior,
    ...behaviorConfig,
    columnWidth: field.columnWidth,
    label: hasLongText ? field.longText : field.displayText,
    name: field.internalName,
    ...(isAutoCalculatedField(field?.type) && {
      appearance: APPEARANCE_TYPES.FILL_READ,
      style: AUTO_CALC_FIELD_STYLE,
    }),
    rules: mergeUniqueRules(fieldConfig.rules, initialBehavior.rules),
  };
}

export const shouldIncludeField = (field, groupId) => {
  const matchesGroup = groupId ? field.groupId === groupId : !field.groupId;
  return matchesGroup && !field.parentTable?.id && field.enabled;
};

export function mapFields(config, formId, operationType, groupId = false) {
  return _.filter(config.form.fields, (field) => shouldIncludeField(field, groupId))
    .sort(sortFn)
    .map((field) => {
      // eslint-disable-next-line no-param-reassign
      if (!formId) formId = field.formId;
      return fieldIterator(field, config);
    })
    .map((field) => (field ? mapField(field, config, operationType) : null))
    .filter(Boolean);
}
